import { Component, OnInit, OnDestroy, Inject, LOCALE_ID, EventEmitter, Output, ElementRef, HostListener, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, map, takeUntil, take, shareReplay, catchError, finalize } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject, of, forkJoin } from 'rxjs';
import { OmniSearchService } from '@fitness-force/api';
import { OmniSearchProvider, OmniSearchResponseByTenantId, OmniSearchResponseForClient } from './client-omnisearch.interfaces';
import { Store } from '@ngrx/store';
import { STAFF_STATE, TENANTS_INFO } from 'apps/ff-frontend/src/app/state/app.state';
import { TENANT_MODEL, TENANTSINTERFACE } from '@fitness-force/models';
import { STAFFINTERFACE, STATE_STAFF_LIST } from '@fitness-force/models';
import { Client } from '@fitness-force/models';
import { AuthSevices } from '@fitness-force/api';
import { ClassBookingRequest, ClassBookingResponse, OmniSearchRequestByProvider, OmniSearchRequestByTenantId } from '@fitness-force/models';
import { ClassBookingService } from '@fitness-force/api';
import { TranslateService } from '@ngx-translate/core';
import { ToastMessageService } from '@fitness-force/api';
import { ClientStatus } from '@fitness-force/enum';
import { StaffStoreService } from 'libs/api/src/lib/staff/staff-store.service';
import { TranslateModule } from '@ngx-translate/core';
import { StyleClassModule } from 'primeng/styleclass';
import { ShortNamePipe } from '@fitness-force/pipes/short-name';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { DrawerModule } from 'primeng/drawer';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RippleModule } from 'primeng/ripple';
import { ScrollerModule } from 'primeng/scroller';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { TagModule } from 'primeng/tag';
import { BookAppointmentComponent } from '../schedule/appointment/book-appointment/book-appointment.component';
import { ClientSearchSidebarComponent } from './client-search-sidebar.component';
import { CalendarComponent } from '../calendar/calendar.component';
import { RouteStateService } from '../../router-guard/route-state-service.service';

// Interface for UI compatibility
interface UIClientSearchResponse extends OmniSearchResponseByTenantId {
  searchResults?: {
    clients: {
      items: Client[];
      statistics: Array<{
        label: string;
        key: string;
        value: number;
        items: Client[];
      }>;
      total_items: number;
    };
  };
}

@Component({
  selector: 'client-omnisearch',
  templateUrl: './client-omnisearch.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    DrawerModule,
    AvatarModule,
    ScrollPanelModule,
    CheckboxModule,
    TagModule,
    RippleModule,
    StyleClassModule,
    ShortNamePipe,
    TranslateModule,
    ScrollerModule,
    BookAppointmentComponent,
    ClientSearchSidebarComponent,
    DialogModule,
    CalendarComponent,
    ProgressSpinnerModule
  ]
})
export class ClientOmnisearchComponent implements OnInit, OnDestroy {
  @Output() prospectEvent = new EventEmitter<{formType: string, searchData?: { name?: string; email?: string; mobile?: string }}>();
  @Output() clientProfileEvent = new EventEmitter<{clientId: number,tab:string}>();
  showAllProspects = false;
  showResultsForAllCentres = false;
  expandedClientIds: Set<number> = new Set();
  active1 = 0;
  searchControl = new FormControl('');
  searchResults: UIClientSearchResponse | null = null;
  loading = false;
  private destroy$ = new Subject<void>();
  selectedTenant: TENANT_MODEL | null = null;
  allTenants: TENANT_MODEL[] = [];
  selectedFilter: any = null;
  allClientsCount = 0;
  currentCentreClientsCount = 0;
  allStaffs: STAFFINTERFACE[] = [];
  showResultDropdown = new BehaviorSubject<boolean>(false);
  showBookAppointmentPopup:boolean = false;
  clientId: number;
  selectedDateTime: Date = new Date();
  tenantId: number;
  boundGoToClientProfile = this.goToClientProfile.bind(this);

  // New properties for recent searches
  recentSearches: string[] = [];
  recentlyViewedClients: Client[] = [];
  showRecentSearches = new BehaviorSubject<boolean>(false);
  readonly MAX_RECENT_ITEMS = 10;
  readonly MAX_RECENT_VIEWED_ITEMS = 10;
  hideSearchDropdown = true;
  showBookClassPopup = false;

  // Add new property for loading state
  isBookingClass = false;
  
  // Staff cache to prevent repeated API calls
  private staffCache: Map<number, Observable<STAFFINTERFACE>> = new Map();
  // Maximum cache size to prevent memory bloat
  private readonly MAX_CACHE_SIZE = 100;


  constructor(private omniSearchService: OmniSearchService,
    private store: Store,
    @Inject(LOCALE_ID) public locale: string,
    private elementRef: ElementRef,
    public cd: ChangeDetectorRef,
    private authService: AuthSevices,
    private classBookingService: ClassBookingService,
    private translate: TranslateService,
    private toastMessageService: ToastMessageService,
    private staffStoreService: StaffStoreService,
    private routeStateService: RouteStateService
    ) {
    this.loadRecentSearches();
    this.loadRecentlyViewedClients();
  }

  ngOnInit() {
    this.store.select(TENANTS_INFO).pipe(
      takeUntil(this.destroy$)
    ).subscribe((tenant: TENANTSINTERFACE) => {
      if (tenant.selected_tenant.id > 0) {
        this.tenantId = tenant.selected_tenant.id;
        this.selectedTenant = tenant.selected_tenant;
        this.allTenants = tenant.accessed_tenant;
      }
    });
    
    // this.store.select(STAFF_STATE).pipe(
    //   takeUntil(this.destroy$)
    // ).subscribe((staff: STATE_STAFF_LIST) => {
    //   this.allStaffs = staff.staffListAll;
    // });
    
    this.setupSearchListener();
    this.routeStateService.closeOmniSearchSidebarAll$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((value: boolean) => {
      if(value) {
        this.showResultDropdown.next(false);
        this.showRecentSearches.next(false);
        this.showAllProspects = false;
        this.selectedFilter = null;
        this.showResultsForAllCentres= false;
        this.cd.detectChanges();
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.searchControl.reset();
    
    // Clean up BehaviorSubject subscriptions
    this.showRecentSearches.complete();
    this.showResultDropdown.complete();
    
    // Clear the cache to free memory
    this.staffCache.clear();
    
    this.destroy$.complete();
  }

  private setupSearchListener() {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(query => {
        if (query && query.length >= 3) {
          this.performSearch(query);
        } else {
          this.searchResults = null;
          this.expandedClientIds.clear();
          this.staffCache.clear(); // Clear the cache when search is cleared
          this.showRecentSearches.next(true);
          this.showResultDropdown.next(false);
        }
      });
  }

  private async performSearch(query: string) {
    try {
      this.loading = true;
      const request : OmniSearchRequestByTenantId = {
        language: this.locale,
        query: query,
        top_n_results: 5
      };
      this.omniSearchService.SearchByTenantId(this.tenantId.toString(),request).pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: (response: OmniSearchResponseByTenantId) => {
          this.showResultDropdown.next(true);
          this.searchResults = response || null;
          this.expandedClientIds.clear();
          this.processSearchResults();
          
          // Add the search term to recent searches when we get successful results
          if (this.searchResults?.search_results?.total_items > 0) {
            this.addToRecentSearches(query);
          }
        },
        error: (msg: any) => {
          this.showResultDropdown.next(true);
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        }
      });
    } catch (error) {
      this.showResultDropdown.next(true);
      this.loading = false;
    }
  }

  private processSearchResults() {
    this.showRecentSearches.next(false);
    if (this.searchResults?.search_results?.tenant_level_clients?.length) {
     // this.addToRecentSearches(this.searchControl.value || '');
      const tenantClients = this.searchResults.search_results.tenant_level_clients[0]; // Current tenant's data
      
      // Get all clients
      const allClients: Client[] = [
        ...tenantClients.prospects_and_trials.items,
        ...tenantClients.active_clients_and_non_members.items,
        ...tenantClients.ex_clients_and_non_members.items,
        ...tenantClients.blocked_clients.items
      ];

      // Prefetch staff details for all clients
      this.prefetchStaffDetails(allClients);

      this.allClientsCount = this.searchResults.search_results.total_items;
      this.currentCentreClientsCount = tenantClients.prospects_and_trials.total_items + tenantClients.active_clients_and_non_members.total_items + tenantClients.ex_clients_and_non_members.total_items;

      // Create statistics array for UI using the pre-categorized data
      const clientStatistics = [
        {
          label: 'PROSPECT',
          key: 'prospects',
          value: tenantClients.prospects_and_trials.total_items,
          items: tenantClients.prospects_and_trials.items
        },
        {
          label: 'ACTIVE',
          key: 'active',
          value: tenantClients.active_clients_and_non_members.total_items,
          items: tenantClients.active_clients_and_non_members.items
        },
        {
          label: 'EX_CLIENT',
          key: 'ex',
          value: tenantClients.ex_clients_and_non_members.total_items,
          items: tenantClients.ex_clients_and_non_members.items
        },
        {
          label: 'BLOCKED',
          key: 'blocked',
          value: tenantClients.blocked_clients.total_items,
          items: tenantClients.blocked_clients.items
        }
      ];

      // Create a compatible structure for the existing UI
      this.searchResults = {
        ...this.searchResults,
        searchResults: {
          clients: {
            items: allClients,
            statistics: clientStatistics,
            total_items: this.allClientsCount
          }
        }
      };

      //this.selectedFilter = clientStatistics[0];
    }
  }

  getSelectedFilterClients(filter: any) {
    return filter.items || [];
  }

  BackToAllData() {
    this.showAllProspects = false;
    this.selectedFilter = null;
    this.cd.detectChanges();
  }


  toggleAllProspects(selectedFilter: any) {
    this.showAllProspects = !this.showAllProspects;
    if(this.showAllProspects && selectedFilter.value != selectedFilter.items.length) {
      this.selectedFilter = selectedFilter;
      let request: OmniSearchRequestByProvider = {
        query: this.searchControl.value || '',
        must_not_include_client_ids: [],
        pagination: { page_number: 1, page_size: selectedFilter.value },
        language: this.locale
      };
      var provider:string='';
      request.must_not_include_client_ids = selectedFilter.items.map((client: Client) => client.clt_id);
      if(selectedFilter.label.toLowerCase() === 'prospect') {
        provider = OmniSearchProvider[OmniSearchProvider.ProspectsAndTrials];
      }
      else if(selectedFilter.label.toLowerCase() === 'active') {
        provider = OmniSearchProvider[OmniSearchProvider.ActiveClientsAndNonMembers];
      }
      else if(selectedFilter.label.toLowerCase() === 'ex_client') {
        provider = OmniSearchProvider[OmniSearchProvider.ExClientsAndNonMembers];
      }
      else if(selectedFilter.label.toLowerCase() === 'blocked') {
        provider = OmniSearchProvider[OmniSearchProvider.BlockedClients];
      }
      this.omniSearchService.SearchByProvider(this.tenantId,provider.toString(),request).subscribe({
        next: (response: OmniSearchResponseForClient) => {
          if(response.items.length > 0) {
            if(provider === OmniSearchProvider[OmniSearchProvider.ProspectsAndTrials]) {
              this.selectedFilter.items = [...this.selectedFilter.items, ...response.items];
            }
            else if(provider === OmniSearchProvider[OmniSearchProvider.ActiveClientsAndNonMembers]) {
              this.selectedFilter.items = [...this.selectedFilter.items, ...response.items];
            }
            else if(provider === OmniSearchProvider[OmniSearchProvider.ExClientsAndNonMembers]) {
              this.selectedFilter.items = [...this.selectedFilter.items, ...response.items];
            }
            else if(provider === OmniSearchProvider[OmniSearchProvider.BlockedClients]) {
              this.selectedFilter.items = [...this.selectedFilter.items, ...response.items];
            }
          }
          else {
            this.showAllProspects = !this.showAllProspects;
          }
          this.cd.detectChanges();
        },
      });
    }
    else {
      this.selectedFilter = selectedFilter;
    }
  }

  toggleResultsForAllCentres() {
    if (this.allClientsCount > 0) {
      this.showResultsForAllCentres = true;
      this.addToRecentSearches(this.searchControl.value || '');
      this.cd.detectChanges();
    }
  }

  isClientExpanded(clientId: number): boolean {
    return this.expandedClientIds.has(clientId);
  }

  toggleClientDetails(clientId: number, event: Event) {
    event.stopPropagation(); // Prevent event bubbling
    if (this.expandedClientIds.has(clientId)) {
      this.expandedClientIds.delete(clientId);
    } else {
      this.expandedClientIds.add(clientId);
      
      // Prefetch staff details for this client if not already cached
      const client = this.searchResults?.searchResults?.clients?.items?.find((c: Client) => c.clt_id === clientId);
      if (client && client.clt_rep_id && !this.staffCache.has(client.clt_rep_id)) {
        this.getSalesRep(client.clt_rep_id).pipe(
          takeUntil(this.destroy$)
        ).subscribe();
      }
    }
  }

  getSalesRep(staffId: number): Observable<STAFFINTERFACE> {
    if (!staffId) return of(null as unknown as STAFFINTERFACE);
    
    // Check cache first
    if (!this.staffCache.has(staffId)) {
      // If not in cache, fetch and store the result
      this.staffCache.set(
        staffId,
        this.staffStoreService.getStaffById(staffId).pipe(
          // Share the response with all subscribers to prevent multiple API calls
          shareReplay(1),
          // Handle errors gracefully
          catchError(() => of(null as unknown as STAFFINTERFACE)),
          // Ensure we don't exceed the max cache size
          finalize(() => this.limitCacheSize())
        )
      );
    }
    
    return this.staffCache.get(staffId) || of(null as unknown as STAFFINTERFACE);
  }
  
  /**
   * Prefetch staff details for all visible clients to improve UX
   * @param clients List of clients to prefetch staff details for
   */
  prefetchStaffDetails(clients: Client[]) {
    if (!clients || clients.length === 0) return;
    
    // Get unique staff IDs
    const staffIds = new Set<number>();
    clients.forEach(client => {
      if (client.clt_rep_id && !this.staffCache.has(client.clt_rep_id)) {
        staffIds.add(client.clt_rep_id);
      }
    });
    
    // If no new staff details to fetch, return
    if (staffIds.size === 0) return;
    
    // Prefetch all required staff details in parallel
    const requests: Observable<STAFFINTERFACE>[] = [];
    staffIds.forEach(id => {
      const request = this.staffStoreService.getStaffById(id).pipe(
        shareReplay(1),
        catchError(() => of(null as unknown as STAFFINTERFACE))
      );
      this.staffCache.set(id, request);
      requests.push(request);
    });
    
    // Execute all requests in parallel and ignore the result
    if (requests.length > 0) {
      forkJoin(requests).pipe(
        takeUntil(this.destroy$)
      ).subscribe();
    }
  }
  
  /**
   * Limit the cache size to prevent memory bloat
   */
  private limitCacheSize() {
    if (this.staffCache.size > this.MAX_CACHE_SIZE) {
      // Get the oldest keys to remove
      const keysToDelete = Array.from(this.staffCache.keys()).slice(0, this.staffCache.size - this.MAX_CACHE_SIZE);
      keysToDelete.forEach(key => this.staffCache.delete(key));
    }
  }

  getClientStatusText(status: string): string {
    return status.toUpperCase();
  }

  openPopup(formType: string) {
    const searchData = this.parseSearchTerm(this.searchControl.value || '');
    this.prospectEvent.emit({formType: formType, searchData: searchData});
  }

  goToClientProfile(clientId: number, tab: string) {
    const client = this.searchResults?.searchResults?.clients?.items?.find((c: Client) => c.clt_id === clientId);
    if (client) {
      this.addToRecentlyViewed(client);
      this.addToRecentSearches(this.searchControl.value || '');
    }

    if(tab !== 'overview' && tab !== 'transactions' && tab !== 'subscriptions') {
      this.showResultDropdown.next(false);
      this.showRecentSearches.next(false);
      
      // If navigating away, consider clearing the cache to free memory
      if (this.staffCache.size > 20) {
        this.staffCache.clear();
      }
    }
    this.clientProfileEvent.emit({clientId: clientId, tab: tab});
  }

  toggleResultDropdown() {
    this.showResultDropdown.next(!this.showResultDropdown.value);
  }

  showBookAppointment(clientId: number) {
    this.showBookAppointmentPopup = true;
    this.clientId = clientId;
  }

  CloseBookAppointment(event: any) {
    this.showBookAppointmentPopup = false;
    this.cd.detectChanges();
  }

  closeSidebar() {
    this.showResultsForAllCentres= false;
    this.cd.detectChanges();
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: Event) {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    const clickedOnAppointmentPopup = (event.target as HTMLElement).closest('.appointment-popup');
    const clickedOnClientsProfileSidebar = (event.target as HTMLElement).closest('.clients-profile-sidebar');
    const clickedOnClientInfoSidebar = (event.target as HTMLElement).closest('.client-info-sidebar');
    const clickedOnProspectForm = (event.target as HTMLElement).closest('.prospect-form');
    const clickedOnDropdown = (event.target as HTMLElement).closest('.omnisearch-dropdown');
    const clickedOnButton = (event.target as HTMLElement).closest('button') || (event.target as HTMLElement).closest('.p-button');
    const clickedOnAvatar = (event.target as HTMLElement).closest('.p-avatar');
    const clickedOnSidebar = (event.target as HTMLElement).closest('.p-sidebar') || 
                            (event.target as HTMLElement).closest('.p-sidebar-mask') ||
                            (event.target as HTMLElement).closest('.p-dialog') ||
                            (event.target as HTMLElement).closest('.p-dialog-mask');
    const clickedOnHeader = (event.target as HTMLElement).closest('header') ||
                           (event.target as HTMLElement).closest('.header') ||
                           (event.target as HTMLElement).closest('.app-header') ||
                           (event.target as HTMLElement).closest('.main-header');

    const clickedOnUIButton = (event.target as HTMLElement).closest('.p-button') ;
    

    // Check if clicked on sidebar menu item
    const clickedOnSidebarMenuItem = (event.target as HTMLElement).closest('.app-sidebar__submenu a');
    if (clickedOnSidebarMenuItem) {
      this.showResultDropdown.next(false);
      this.showRecentSearches.next(false);
      this.cd.detectChanges();
      return;
    }
    
    // Don't close if clicked inside the component or other UI elements
    if (clickedInside || 
        clickedOnAppointmentPopup || 
        clickedOnClientsProfileSidebar || 
        clickedOnClientInfoSidebar || 
        clickedOnProspectForm ||
        clickedOnDropdown ||
        clickedOnButton ||
        clickedOnAvatar ||
        clickedOnSidebar ||
        clickedOnUIButton ||
        clickedOnHeader) {
      return;
    }

    // Close if clicked outside all relevant elements
    if (this.showResultDropdown.value || this.showRecentSearches.value) {
      this.showResultDropdown.next(false);
      this.showRecentSearches.next(false);
      this.cd.detectChanges();
    }
  }

  onSearchFocus() {
        if (this.searchControl.value && this.searchControl.value.length > 2) {
          this.showResultDropdown.next(true);
          this.showRecentSearches.next(false);
        } else {
          this.showResultDropdown.next(false);
          this.showRecentSearches.next(true);
        }
  }

  // Load recent searches from localStorage
  private loadRecentSearches() {
    try {
      const searches = localStorage.getItem('recentSearches_'+this.authService.loggedInStaffId);
      this.recentSearches = searches ? (JSON.parse(searches) as string[]) : [];
    } catch (error) {
      console.error('Error loading recent searches:', error);
      this.recentSearches = [];
    }
  }

  // Load recently viewed clients from localStorage
  private loadRecentlyViewedClients() {
    const clients = localStorage.getItem('recentlyViewedClients_'+this.authService.loggedInStaffId);
    this.recentlyViewedClients = clients ? JSON.parse(clients) : [];
  }

  // Add a search term to recent searches
  private addToRecentSearches(term: string) {
    if (!term) return;
    
    // Remove the term if it already exists to avoid duplicates
    const existingSearches = this.recentSearches.filter(s => s !== term);
    
    // If we've reached the maximum, remove the last item
    if (existingSearches.length >= this.MAX_RECENT_ITEMS) {
      existingSearches.pop(); // Remove the last item
    }
    
    // Add the new term at the beginning
    this.recentSearches = [term, ...existingSearches];
    
    localStorage.setItem('recentSearches_'+this.authService.loggedInStaffId, JSON.stringify(this.recentSearches));
  }

  // Add a client to recently viewed
  private addToRecentlyViewed(client: Client) {
    if (!client) return;
    
    // Remove the client if it already exists to avoid duplicates
    const existingClients = this.recentlyViewedClients.filter(existingClient => existingClient.clt_id !== client.clt_id);
    
    // If we've reached the maximum, remove the last item
    if (existingClients.length >= this.MAX_RECENT_VIEWED_ITEMS) {
      existingClients.pop(); // Remove the last item
    }
    
    // Add the new client at the beginning
    this.recentlyViewedClients = [client, ...existingClients];
    
    localStorage.setItem('recentlyViewedClients_'+this.authService.loggedInStaffId, JSON.stringify(this.recentlyViewedClients));
  }

  // Method to handle recently viewed client click
  onRecentClientClick(client: any) {
    this.goToClientProfile(client.clt_id, 'overview');
  }

  onRecentSearchClick(term: string) {
    this.searchControl.setValue(term);
    this.performSearch(term);
  }

  getClientStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'prospect':
        return 'text-orange-500 bg-orange-50';
      case 'client':
        return 'text-green-500 bg-green-50';
      case 'exclient':
        return 'text-red-500 bg-red-50';
      case 'blocked':
        return 'text-bluegray-500 bg-bluegray-50';
      default:
        return 'text-gray-500 bg-gray-50';
    }
  }

  getClientStatusHtml(status: string): any {
    var clientStatus = {class: '', text: ''};
    switch (status) {
      case ClientStatus.Prospect.toString():
      case ClientStatus[ClientStatus.Prospect]:
        clientStatus.class = 'bg-pink-50 text-green-500';
        clientStatus.text = 'PROSPECT';
        break;
      case ClientStatus.Trial.toString():
      case ClientStatus[ClientStatus.Trial]:
        clientStatus.class = 'bg-cyan-50 text-cyan-500';
        clientStatus.text = 'TRIAL';
        break;
      case ClientStatus.Client.toString():
      case ClientStatus[ClientStatus.Client]:
        clientStatus.class = 'bg-green-50 text-green-500';
        clientStatus.text = 'MEMBER';
        break;
      case ClientStatus.ClientNonMember.toString():
      case ClientStatus[ClientStatus.ClientNonMember]:
        clientStatus.class = 'bg-yellow-50 text-yellow-500';
        clientStatus.text = 'NON-MEMBER';
        break;
      case ClientStatus.ExClient.toString():
      case ClientStatus[ClientStatus.ExClient]:
        clientStatus.class = 'surface-50 text-500';
        clientStatus.text = 'EX-MEMBER';
        break;
      case ClientStatus.BlockedClient.toString():
      case ClientStatus[ClientStatus.BlockedClient]:
        clientStatus.class = 'bg-bluegray-50 text-bluegray-500';
        clientStatus.text = 'TERMINATED';
        break;
       case ClientStatus.ExClientNonMember.toString():
       case ClientStatus[ClientStatus.ExClientNonMember]:
        clientStatus.class = 'surface-100 text-800';
        clientStatus.text = 'EX-CLIENT NON-MEMBER';
        break;
      default:
        return {class: '', text: ''};
    }
    return clientStatus;
  }

  showBookClass(clientId: number) {
    this.showBookClassPopup = true;
    this.clientId = clientId;
  }

  closePopup(event: any) {
    this.showBookClassPopup = false;
  }

  onCancelSearch() {
    this.searchControl.reset();
    this.showRecentSearches.next(false);
    this.showResultDropdown.next(false);
    this.cd.detectChanges();
  }

  getEventData(event: any) {
    this.isBookingClass = true;
    let classBookingRequest: ClassBookingRequest = {} as ClassBookingRequest;
    classBookingRequest.client_ids = [this.clientId];
    classBookingRequest.add_to_waitlist = false;
    let promise: Promise<boolean> = new Promise((resolve, reject) => {
      this.classBookingService.BookClientsToClass(this.tenantId, event.classData.id, classBookingRequest).subscribe({
        next: (data: ClassBookingResponse) => {
          this.translate.get('SCHEDULE.VIEWCLASS.MSG.CLIENT_ADDDED_MSG').subscribe((translation) => {
            this.toastMessageService.showSuccess(translation);
          });
          this.showBookClassPopup = false;
          this.isBookingClass = false;
          resolve(true);
        },
        error: (error: any) => {
          this.toastMessageService.showError(error);
          this.isBookingClass = false;
          resolve(false);
        },
      });
    });
    return promise;
  }

  // Add this new method to parse search term
  private parseSearchTerm(searchTerm: string): { name?: string; email?: string; mobile?: string } {
    const result: { name?: string; email?: string; mobile?: string } = {};
    
    if (!searchTerm) return result;

    // Email pattern
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    // Mobile pattern (basic pattern, can be adjusted based on requirements)
    const mobilePattern = /^\+?[\d\s-]{8,}$/;

    if (emailPattern.test(searchTerm)) {
      result.email = searchTerm;
    } else if (mobilePattern.test(searchTerm.replace(/[\s-]/g, ''))) {
      result.mobile = searchTerm.replace(/[\s-]/g, '');
    } else {
      result.name = searchTerm;
    }

    return result;
  }

  getCenterName(client: Client): string {
    const center = this.allTenants.find(tenant => tenant.id === client.clt_home_club_tenant_id || tenant.id === client.clt_createdin_tenant_id);
    return center ? center.name_primary_language || center.name_secondary_language || '' : '';
  }

} 